<script setup lang="ts">
import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'

import type { GalleryBlock, GalleryColumns, GalleryImageItem } from '../../types'

const props = defineProps<{
  block: GalleryBlock
  isFixedLayout?: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:block', block: GalleryBlock): void
}>()

const { t } = useI18n()

const updateField = <K extends keyof GalleryBlock>(field: K, value: GalleryBlock[K]) => {
  emit('update:block', { ...props.block, [field]: value })
}

const columnsOptions: GalleryColumns[] = [1, 2, 3]

const emptyImage = (): GalleryImageItem => ({ type: 'image', src: '', alt: '', link: '' })

const setColumns = (columns: GalleryColumns) => {
  const items = props.block.items.slice()
  while (items.length < columns) items.push(emptyImage())
  emit('update:block', { ...props.block, columns, items })
}

const gapValue = computed({
  get: () => [props.block.gap],
  set: ([next]) => updateField('gap', Math.max(0, Math.min(80, next)))
})
</script>

<template>
  <Teleport to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="block.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => updateField('blockBackgroundColor', v)"
        />
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.layoutBlocks')">
        <div v-if="!isFixedLayout" class="space-y-1.5">
          <Label class="text-xs font-medium text-foreground">
            {{ t('pages.email.emailTemplateCreatePage.gallery.columns') }}
          </Label>
          <Tabs
            :model-value="String(block.columns)"
            @update:model-value="(v) => setColumns(Number(v) as GalleryColumns)"
          >
            <TabsList class="w-full">
              <TabsTrigger v-for="opt in columnsOptions" :key="opt" :value="String(opt)" class="flex-1">
                {{ opt }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <Label class="text-xs font-medium text-foreground">
              {{ t('pages.email.emailTemplateCreatePage.gallery.gap') }}
            </Label>
            <span class="text-[11px] tabular-nums text-muted-foreground">{{ block.gap }}px</span>
          </div>
          <Slider v-model="gapValue" :min="0" :max="80" :step="4" />
        </div>
      </EmailPanelSection>
    </div>
  </Teleport>
</template>
